import { useAutoAnimate } from "@formkit/auto-animate/react";
import { CheckIcon, CloseIcon, DisputeIcon, DurationIcon } from "@xola/icons";
import { Alert, Checkbox, flash, Spinner, SubmitButton, Tooltip } from "@xola/ui-kit";
import { useSize } from "ahooks";
import axios, { AxiosRequestConfig } from "axios";
import clsx from "clsx";
import { DateFormat, formatDate } from "common";
import { inRange, intersection, isEmpty, isEqual, sortBy, startCase } from "lodash";
import pluralize from "pluralize";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "react-query";
import { Event, EventGuide, EventGuideStatus, EventGuideStatusCopy, Guide, Timeslot } from "types";
import { useDebouncedCallback } from "use-debounce";
import { FilterItem } from "../../Filters/ListFilter";
import { api } from "@/api/api";
import { getResponseErrorMessage } from "@/api/interceptors";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { TableSkeleton } from "@/components/Common/Skeleton/TableSkeleton";
import { TimeslotParams, UpdateParams, updateTimeslotCache } from "@/components/Event/EditEventCapacityModal";
import { MultiSelectPopover } from "@/components/MultiSelectPopover";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useInvalidate } from "@/hooks/useInvalidation";
import { stringIncludes } from "@/hooks/useSearch";
import { useSelection } from "@/hooks/useSelection";
import { useGuides } from "@/hooks/useSellerResource";
import { getStart } from "@/models/Event";
import { isOverlappingAllowed, needForceConfirm } from "@/models/Guide";
import { useDashboardFilterStore } from "@/stores/dashboardFilterStore";
import { DashboardMode } from "@/types/Dashboard";

// TODO: Same with AssignGuideModal
const getParams = (events: Event[], timeslots: Timeslot[], isVisible = false) => {
    const params: any = {};

    if (isVisible) {
        if (events.length > 0) {
            params.events = events.map((event) => event.id).join(",");
        }

        for (const [index, timeslot] of timeslots.entries()) {
            if (!timeslot.arrivalDatetime) {
                continue;
            }

            params[`intervals[${index}][start]`] = formatDate(timeslot.arrivalDatetime, DateFormat.DATE_TIME_UTC_ISO);
            params[`intervals[${index}][end]`] = formatDate(timeslot.end, DateFormat.DATE_TIME_UTC_ISO);
        }
    }

    return isEmpty(params) ? null : params;
};

interface AssignGuidePopoverProps {
    events: Event[];
    timeslots: Timeslot[];
    isVisible: boolean;
    target: JSX.Element | null;
    popoverParentId?: string;
    onClose: () => void;
    onCreatedEvent?: (timeslot: Timeslot, event: Event) => void;
}

export const AssignGuidePopover = ({
    events,
    timeslots,
    isVisible,
    target,
    popoverParentId,
    onClose,
    onCreatedEvent,
}: AssignGuidePopoverProps) => {
    const invalidate = useInvalidate();

    const [isHideUnavailable, setIsHideUnavailable] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");
    const [partiallySelectedGuideId, setPartiallySelectedGuideId] = useState<ID | null>(null);
    const [forceConfirmGuides, setForceConfirmGuides] = useState<ID[]>([]);
    const [timeslotsToUpdate, setTimeslotsToUpdate] = useState<
        Array<{ timeslotParams: TimeslotParams; updateParams: UpdateParams }>
    >([]);

    const [animationRef] = useAutoAnimate({});
    const size = useSize(document.querySelector(".ui-no-events"));
    const assignGuidesInFlight = useRef<Set<string>>(new Set());
    const unassignGuidesInFlight = useRef<Set<string>>(new Set());

    useEffect(() => {
        const assignGuidesInFlightCurrent = assignGuidesInFlight.current;
        const unassignGuidesInFlightCurrent = unassignGuidesInFlight.current;

        return () => {
            // Clean tracker when this unmounts
            assignGuidesInFlightCurrent.clear();
            unassignGuidesInFlightCurrent.clear();
        };
    }, []);

    const { isGranted } = useAuthorization();

    const mode = useDashboardFilterStore((state) => state.getMode());
    const isWeekMode = mode === DashboardMode.WEEK;

    const guideParams = getParams(events, timeslots, isVisible);
    const { guides, isLoadingGuides } = useGuides(guideParams, { enabled: isVisible });

    const sortedGuides = useMemo(() => {
        const filteredGuides = isHideUnavailable
            ? guides.filter((guide) => guide?.availability?.available === true)
            : [...guides];

        return sortBy(filteredGuides, "name");
    }, [guides, isHideUnavailable]);
    const assignedGuides = getAssignedGuides(events) ?? [];

    const isGuideEnabled = (guide: Guide) => {
        return isGuideAvailable(guide) || assignedGuides?.includes(guide.id) || isGuideEnabledAndOverlapping(guide);
    };

    const filteredGuides = searchTerm
        ? sortedGuides.filter((guide) => stringIncludes(searchTerm, guide.name))
        : sortedGuides;
    const isAllGuidesNotEnabled = filteredGuides.every((guide) => !isGuideEnabled(guide));

    const handleSearch = (text: string): void => setSearchTerm(text);
    const selection = useSelection(filteredGuides, true, { all: false, selected: [] }, false);

    useEffect(() => {
        setSearchTerm("");
        selection.setState({ all: false, selected: assignedGuides });
        const assignGuidesInFlightCurrent = assignGuidesInFlight.current;
        const unassignGuidesInFlightCurrent = unassignGuidesInFlight.current;

        if (!isVisible) {
            assignGuidesInFlightCurrent.clear();
            unassignGuidesInFlightCurrent.clear();
        }
        // TODO: probably key={isVisible} to reset state? what about the useEffect below?
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible]);

    const selectedGuides = guides.filter((guide) => {
        const alreadyAssigned = assignedGuides.includes(guide.id);
        if (alreadyAssigned) {
            const assignedEvents = getAssignedEvents(events, guide.id);
            for (const event of assignedEvents) {
                // current guide is already assigned so delete from in flight
                assignGuidesInFlight.current.delete(getEventGuideKey(event, guide));
            }
        }

        return selection.isSelected(guide) && !alreadyAssigned;
    });

    const unselectedGuides = guides.filter((guide) => {
        const alreadyAssigned = assignedGuides.includes(guide.id);

        return alreadyAssigned && !selection.isSelected(guide);
    });

    const handleChangeHideUnavailable = () => {
        setIsHideUnavailable((previous) => !previous);
    };

    const handleRefetch = async (event?: Event) => {
        invalidate("events", event?.id);
        invalidate("guides");
    };

    const assignGuides = async (event: Event) => {
        const errorMessages = [];
        for (const guide of selectedGuides) {
            const eventGuideKey = getEventGuideKey(event, guide);
            if (assignGuidesInFlight.current.has(eventGuideKey)) {
                console.log(`Skipping assign ${guide.name} because it is already in progress`);
                continue;
            }

            assignGuidesInFlight.current.add(eventGuideKey);
            const guideName = guide.name?.trim();
            try {
                await api.post(
                    `/events/${event.id}/guides`,
                    { guide: { ...guide, forceConfirm: forceConfirmGuides.includes(guide.id) } },
                    {
                        skipResponseInterceptor: true,
                        // Ignore 409 errors for now usually happens during race conditions
                        validateStatus: (status) => inRange(status, 200, 300) || status === 409,
                    } as AxiosRequestConfig,
                );
            } catch (error: any) {
                const message = getErrorMessage(error) ?? "";
                errorMessages.push(`Error assigning guide ${guideName} ${message}`.trim());
            }
        }

        if (errorMessages.length > 0) {
            flash.show({
                text: errorMessages.join("\n"),
                color: "danger",
                duration: 5000,
            });
        }

        if (selectedGuides.length > 0) {
            await handleRefetch(event);
        }
    };

    const unassignGuides = async (event: Event) => {
        const errorMessages = [];

        for (const guide of unselectedGuides) {
            if (unassignGuidesInFlight.current.has(guide.id)) {
                continue;
            }

            unassignGuidesInFlight.current.add(guide.id);
            try {
                await api.delete(`/events/${event.id}/guides/${guide.id}`, {
                    // Ignore 404 errors for now usually happens during race conditions
                    validateStatus: (status) => inRange(status, 200, 300) || status === 404,
                });
            } catch (error: any) {
                const message = getErrorMessage(error) ?? "";
                errorMessages.push(`Error removing guide ${guide.name?.trim()} ${message}`.trim());
            }

            unassignGuidesInFlight.current.delete(guide.id);
        }

        if (errorMessages.length > 0) {
            flash.show({
                text: errorMessages.join("\n"),
                color: "danger",
                duration: 5000,
            });
        }

        if (unselectedGuides.length > 0) {
            await handleRefetch(event);
        }
    };

    const { mutateAsync: updateEventGuideForAEvent, isLoading: isUpdating } = useMutation(
        async ({ event }: { event?: Event }) => {
            if (event) {
                const timeslotParams = {
                    timeslot: null,
                    event: event,
                    productId: null,
                    startDate: null,
                };

                const updateParams = {
                    isGranted,
                };

                await assignGuides(event);
                await unassignGuides(event);

                setTimeslotsToUpdate((values) => {
                    if (values.length > 0) {
                        return [
                            ...values.map((element) => {
                                // Avoid having the same data in the array to do fewer API calls/cache updates if we update the slot for the same event.
                                const existingSlotParams = element.timeslotParams.event?.id === timeslotParams.event.id;
                                return existingSlotParams ? { timeslotParams, updateParams } : element;
                            }),
                        ];
                    }

                    return [{ timeslotParams, updateParams }];
                });
            }
        },
    );

    const { mutateAsync: createEvent, isLoading: isCreatingEvent } = useMutation(
        async ({ timeslot }: { timeslot: Timeslot }) => {
            const updatedGuideList = guides.filter((guide) => selection.isSelected(guide));
            try {
                for (const guide of updatedGuideList) {
                    assignGuidesInFlight.current.add(guide.id);
                }

                const { data: event } = await api.post<Event>(
                    "/events",
                    getCreateEventPayload(timeslot, updatedGuideList),
                    { skipResponseInterceptor: true } as AxiosRequestConfig,
                );
                return { event };
            } catch (error: any) {
                const message = getErrorMessage(error) ?? "";
                flash.show({
                    text: `Failed to assign ${pluralize("guide", updatedGuideList.length)}. ${message}`.trim(),
                    color: "danger",
                    duration: 5000,
                });
            }

            for (const guide of updatedGuideList) {
                selection.select({ id: guide.id });
                assignGuidesInFlight.current.delete(guide.id);
            }

            invalidate("guides");
        },
    );

    const updateEventGuides = async () => {
        if (isEqual(selection.state.selected, assignedGuides)) {
            return;
        }

        if (events?.length > 0) {
            for (const event of events) {
                await updateEventGuideForAEvent({ event });
            }
        }

        if (timeslots?.length > 0) {
            // If there are multiple timeslots, then we get here from the month view (TODO: Is it possible month view with existing events?)
            for (const timeslot of timeslots) {
                // This is when there is no event. So this will create an event for the timeslot
                const { event } = (await createEvent({ timeslot })) ?? {};
                if (event && timeslots.length === 1) {
                    const timeslotParams = {
                        timeslot,
                        event,
                        productId: timeslot.product,
                        startDate: formatDate(timeslot.arrivalDatetime),
                    };

                    const updateParams = {
                        isGranted,
                        forceReplaceId: timeslot.id,
                    };
                    await updateTimeslotCache(timeslotParams, updateParams);

                    onCreatedEvent?.(timeslot, event);
                }
            }

            await handleRefetch();
        }
    };

    const debouncedCallback = useDebouncedCallback(updateEventGuides, 200, { leading: false, trailing: true });
    useEffect(() => {
        if (isVisible) {
            debouncedCallback();
        }
    }, [isVisible, debouncedCallback, selection.state.selected]);

    const onCancel = async (e: any) => {
        onClose();
        e.stopPropagation?.();

        if (timeslotsToUpdate.length > 0) {
            for (const slot of timeslotsToUpdate) {
                await updateTimeslotCache(slot.timeslotParams, slot.updateParams);
            }
        }

        // If weekly view and all-day event then we got to invalidate the whole timeslots cache
        if ((isEmpty(timeslots) && isWeekMode) || timeslots?.length > 0) {
            invalidate("timeslots");
        }
    };

    const setConfirmationForGuide = (selectedGuide: Guide) => {
        selection.select(selectedGuide);
        setForceConfirmGuides([...forceConfirmGuides, selectedGuide.id]);
        setPartiallySelectedGuideId(null);
    };

    const isGuidePartiallySelected = (guide: Guide) => {
        return partiallySelectedGuideId === guide.id && !selection.isSelected(guide);
    };

    // Minor UX improvement to avoid unnecessary scrollbars when removing a guide
    // If we are removing guides on a trip then we need `overflow-hidden` so no scrollbar is shown which causes jank
    const isRemovingNoEventsMessage = size && size.width > 0 && size.height > 0 && events.length > 0;

    return (
        <MultiSelectPopover
            saveOnCheck // Save as soon as you select an item instead of having submit buttons
            allowEmptySelection={Boolean(!isAllGuidesNotEnabled) || Boolean(searchTerm && filteredGuides.length > 0)}
            title="Assign Guides"
            isVisible={isVisible}
            selectedCount={selection.state.all ? "All" : selection.state.selected.length}
            target={target}
            zIndex={50}
            placement="bottom-start" // The -start is there so that it doesn't change based on the length of the string being shown, like guide name
            className="w-100"
            popoverParentId={popoverParentId}
            onSearch={handleSearch}
            onCancel={onCancel}
        >
            <div
                ref={animationRef}
                className={clsx(isRemovingNoEventsMessage ? "!overflow-hidden" : "max-h-75 overflow-auto")}
            >
                {isLoadingGuides && (
                    <div className={clsx("p-4")}>
                        <TableSkeleton rows={6} columns={2} className="pb-0" />
                    </div>
                )}
                {events.length === 0 && !isLoadingGuides && <NoEventsMessage />}
                <div className={clsx(isLoadingGuides && "hidden")}>
                    {filteredGuides.length === 0 && <NoSearchResultsMessage />}

                    <MultiSelectPopover.Item className="bg-gray-lighter font-bold">
                        <Checkbox
                            checked={isHideUnavailable}
                            label="Hide unavailable guides"
                            onChange={handleChangeHideUnavailable}
                        />
                    </MultiSelectPopover.Item>

                    {filteredGuides?.map((guide) => {
                        const status = events.length === 1 ? getGuideStatus(events[0].guides, guide) : undefined;

                        const overlapError = getOverlapError(guide);
                        const isSelected = selection.isSelected(guide);
                        const isUpdatingGuide = isUpdating || isCreatingEvent;
                        const isAvailable = guide?.availability?.available;

                        const needForceConfirmation = isGuideEnabledAndOverlapping(guide) && needForceConfirm(guide);
                        const needsConfirmation = isGuidePartiallySelected(guide) && needForceConfirm(guide);

                        return (
                            <MultiSelectPopover.Item
                                key={guide.id}
                                className={clsx(
                                    !isAvailable && "bg-gray-lighter",
                                    "border-b border-gray-lighter font-bold",
                                )}
                            >
                                <FilterItem
                                    label={<GuideLabel guide={guide} isLoading={isLoadingGuides || isUpdatingGuide} />}
                                    isChecked={isSelected}
                                    isEnabled={isGuideEnabled(guide)}
                                    status={
                                        isGuideEnabledAndOverlapping(guide) ? (
                                            // Ask for confirmation if force confirm for overlap is true
                                            needsConfirmation ? (
                                                <ConfirmOverlap
                                                    onConfirm={() => setConfirmationForGuide(guide)}
                                                    onCancel={() => setPartiallySelectedGuideId(null)}
                                                />
                                            ) : (
                                                <Tooltip content={overlapError?.reason} placement="right">
                                                    <DisputeIcon size="large" className="text-warning-dark" />
                                                </Tooltip>
                                            )
                                        ) : (
                                            <GuideStatus status={status} isUpdating={isUpdatingGuide} />
                                        )
                                    }
                                    onClick={() => {
                                        if (isGuideEnabledAndOverlapping(guide) && !isSelected) {
                                            setPartiallySelectedGuideId(guide.id);
                                        }
                                    }}
                                    onChange={() => {
                                        if (!needForceConfirmation || isSelected) {
                                            selection.select(guide);
                                        }
                                    }}
                                />
                            </MultiSelectPopover.Item>
                        );
                    })}
                </div>
            </div>
        </MultiSelectPopover>
    );
};

export const getAssignedGuides = (events: Event[]) => {
    const initialGuideIds = events.length > 0 ? events[0].guides?.map((item) => item.guide.id) : [];
    /* eslint-disable-next-line unicorn/no-array-reduce */
    return events.reduce((assignedGuideIds, event) => {
        const eventGuideIds = event.guides?.map((item) => item.guide.id);
        return intersection(eventGuideIds, assignedGuideIds);
    }, initialGuideIds);
};

export const getAssignedEvents = (events: Event[], guideId: ID) => {
    // Filter all events where given Guide is assigned
    return events.filter((event) => {
        return event.guides?.some((item) => item.guide.id === guideId);
    });
};

export const getEventGuideKey = (event: Event, guide: Guide) => `${event.id}_${guide.id}`;

export const isGuideAvailable = (guide: Guide) => guide.availability?.available ?? true;

interface ConfirmOverlapProps {
    onCancel: () => void;
    onConfirm: () => void;
}

export const ConfirmOverlap = ({ onCancel, onConfirm }: ConfirmOverlapProps) => {
    return (
        <div className="flex space-x-2">
            <SecondaryButton size="small" onClick={onCancel}>
                Undo
            </SecondaryButton>
            <SubmitButton color="primary" className="w-32" size="small" onClick={onConfirm}>
                Confirm Overlap
            </SubmitButton>
        </div>
    );
};

export const getCreateEventPayload = (timeslot: Timeslot, guides: Guide[]) => {
    return {
        experience: { id: timeslot.product },
        quantity: { reserved: timeslot.capacity?.reserved ?? 0 },
        start: formatDate(timeslot.arrivalDatetime, DateFormat.DATE_TIME_UTC_ISO),
        guides: guides.map((guide) => ({ guide })),
    };
};

const eventGuideStatusIconMap = {
    [EventGuideStatus.CONFIRMED]: <CheckIcon size="medium" className="top-0 text-success" />,
    [EventGuideStatus.PENDING]: <DurationIcon size="medium" className="top-0 text-warning" />,
    [EventGuideStatus.DECLINED]: <CloseIcon size="medium" className="top-0 text-danger" />,
};

export const UnavailableGuideLabel = ({ name, isLoading = false }: { name?: string; isLoading: boolean }) => {
    return (
        <span>
            <NoTranslate if={!!name}>{name ?? "No Name"}</NoTranslate>{" "}
            {isLoading ? <Spinner size="tiny" /> : <span className="text-gray-dark">(unavailable)</span>}
        </span>
    );
};

export const getGuideStatus = (guides: EventGuide[], guide: Guide) => {
    return guides.find((eventGuide) => guide.id === eventGuide.guide.id)?.status;
};

export const getOverlapError = (guide: Guide) => {
    return guide.availability?.errors?.find((error: any) => error.code === "assigned_to_overlapping_event");
};

export const GuideLabel = ({ guide, isLoading = false }: { guide: Guide; isLoading: boolean }) => {
    const { name } = guide;
    if (isGuideAvailable(guide) || isOverlappingAllowed(guide)) {
        return (
            <NoTranslate if={!!name} className={clsx("flex w-full items-center justify-between")}>
                {name ?? "No Name"}
            </NoTranslate>
        );
    }

    return <UnavailableGuideLabel name={name} isLoading={isLoading} />;
};

export const isGuideEnabledAndOverlapping = (guide: Guide) => {
    // Guide is overlapping with another event and overlap is allowed.
    const overlapAllowed = guide.overlappingEvents?.enabled ?? false;
    const overlapError = getOverlapError(guide);

    return !!overlapError && overlapAllowed;
};

export const NoEventsMessage = () => {
    return (
        <Alert className="ui-no-events m-2" color="warning">
            <div className="flex whitespace-normal">
                <DisputeIcon className="mt-1 mr-2 flex-shrink-0" size="large" />
                <div>You are about to assign guides to an event with no bookings</div>
            </div>
        </Alert>
    );
};

export const NoSearchResultsMessage = () => {
    return <div className=" p-4 text-center">No results found</div>;
};

export const GuideStatus = ({ status, isUpdating }: { status: EventGuideStatus | undefined; isUpdating: boolean }) => {
    if (isUpdating) {
        return (
            <div className="h-5">
                <Spinner size="tiny" />
            </div>
        );
    }

    if (status) {
        return (
            <Tooltip content={EventGuideStatusCopy[status]} placement="right">
                {eventGuideStatusIconMap[status]}
            </Tooltip>
        );
    }

    return <div className="h-5" />;
};

export const getErrorMessage = (error: any) => {
    let { message }: { message: string } = error;
    if (error.response && axios.isAxiosError(error)) {
        const { data = {} }: any = error.response;
        message = data.field?.guide?.message ?? data.field?.message ?? "";

        if (!message || isEmpty(message.trim())) {
            message = message ?? getResponseErrorMessage(error.response);
        }

        if (data.field) {
            if (data.field.reason === "assigned_to_overlapping_event") {
                if (data.field.event) {
                    const { event } = data.field;
                    const date = formatDate(getStart(event), DateFormat.DATE_TIME_SHORT);
                    message += ` (${event.title} at ${date})`;
                } else {
                    message += ` ${data.field.reason}`;
                }
            }

            if (data.field.start) {
                message += `${startCase(data.field.start)}`;
            }
        }
    }

    return message;
};
